/**
 * Licences tab (#1142): every Pax8 licence against the Books line that bills it. Opens on "Needs action".
 */
import { Chip, cn, EmptyState } from '@patchbay/ui'
import * as React from 'react'
import type { LicenceMarginRow } from '../../../lib/api/generated'
import {
  ChipFilter,
  type Column,
  cellBase,
  countStat,
  GroupedTable,
  groupClasses,
  Legend,
  MarginValue,
  MaybeLink,
  Missing,
  MsrpValue,
  moneyStat,
  numCell,
  pctStat,
  SearchBox,
  StatStrip,
} from './bits'
import {
  ALARM_STATUS,
  isBilledLine,
  LICENCE_CHIPS,
  licenceSummary,
  marginAtRenewal,
  marginLines,
  marginNow,
  matchesSearch,
  money,
  type Notes,
  num,
  perSeat,
  QUIET_STATUS,
  qtyText,
  type SortState,
  sortBy,
} from './model'
import { NoteCell } from './notes'
import { RenewalCell } from './renewal-card'

const COLUMNS: Column[] = [
  { key: 'customer', label: 'Customer', group: '' },
  { key: 'product', label: 'Licence', group: '' },
  { key: 'pax8_qty', label: 'Pax8', group: 'Quantity', numeric: true },
  { key: 'billed_qty', label: 'Billed', group: 'Quantity', numeric: true },
  { key: 'billed_rate', label: 'We bill', group: 'Price vs MSRP', numeric: true },
  { key: 'msrp', label: 'MSRP', group: 'Price vs MSRP', numeric: true },
  { key: 'pct_of_msrp', label: '± MSRP', group: 'Price vs MSRP', numeric: true },
  { key: 'cost_now', label: 'Cost', group: 'Today', numeric: true },
  { key: 'margin_pct_now', label: 'Margin', group: 'Today', numeric: true },
  { key: 'renews', label: 'Renews', group: 'At renewal' },
  { key: 'cost_at_renewal', label: 'Cost', group: 'At renewal', numeric: true },
  { key: 'margin_pct_at_renewal', label: 'Margin', group: 'At renewal', numeric: true },
  { key: 'books_customer', label: 'Books customer', group: 'Books', narrow: true },
  { key: null, label: 'Notes', group: ' ' },
]

const NUMERIC = new Set(['pax8_qty', 'billed_qty', 'billed_rate', 'msrp', 'pct_of_msrp', 'cost_now', 'cost_at_renewal'])

function sortValue(r: LicenceMarginRow, key: string): string | number | null | undefined {
  if (key === 'margin_pct_now') return marginNow(r)
  if (key === 'margin_pct_at_renewal') return marginAtRenewal(r)
  if (NUMERIC.has(key)) return num(r[key as keyof LicenceMarginRow] as string | number | null)
  return r[key as keyof LicenceMarginRow] as string | null | undefined
}

const moneyCell = (v: string | null | undefined) => {
  const n = num(v)
  return n === null ? <Missing /> : money(n)
}

function LicenceRow({ row, notes, cls }: { row: LicenceMarginRow; notes: Notes; cls: string[] }) {
  const billed = isBilledLine(row)
  const bill = num(row.billed_rate)
  const msrp = num(row.msrp)
  const status = row.status ?? null
  const qtyOff = row.billed_qty != null && row.billed_qty !== row.pax8_qty && status !== 'Billed, not in Pax8'
  const c = (i: number, extra?: string) => cn(cellBase, cls[i], extra)
  return (
    <tr data-line={row.key ?? undefined}>
      <td className={c(0, 'font-medium')}>
        <MaybeLink href={row.links?.crm}>{row.customer}</MaybeLink>
      </td>
      <td className={c(1, 'min-w-56')}>
        <MaybeLink href={row.links?.pax8}>{row.product}</MaybeLink>
        {status && !QUIET_STATUS.has(status) ? (
          <Chip tone={ALARM_STATUS.has(status) ? 'danger' : 'neutral'} className="ml-1.5 align-middle" data-status={status}>
            {status}
          </Chip>
        ) : null}
        {row.term ? <span className="block text-xs text-muted-foreground">{row.term}</span> : null}
        {row.books_fix ? <span className="block max-w-[34ch] text-xs text-warning">Books: {row.books_fix}</span> : null}
      </td>
      <td className={c(2, numCell)}>{row.pax8_qty == null ? <Missing /> : qtyText(row.pax8_qty)}</td>
      <td className={c(3, cn(numCell, qtyOff && 'font-semibold text-destructive'))} title={row.qty_mismatch || undefined}>
        {row.billed_qty == null ? (
          <span className="text-xs text-muted-foreground">no Books line</span>
        ) : (
          <MaybeLink href={row.links?.books}>{qtyText(row.billed_qty)}</MaybeLink>
        )}
        {qtyOff && row.qty_mismatch ? <span className="sr-only">. {row.qty_mismatch}</span> : null}
      </td>
      <td className={c(4, numCell)}>{bill === null ? <Missing /> : <MaybeLink href={row.links?.books}>{money(bill)}</MaybeLink>}</td>
      <td className={c(5, numCell)}>{moneyCell(row.msrp)}</td>
      <td className={c(6, numCell)}>
        <MsrpValue
          pctOfMsrp={billed ? num(row.pct_of_msrp) : null}
          detail={billed && bill !== null && msrp !== null ? { bill, msrp, unit: perSeat(row) } : null}
        />
      </td>
      <td className={c(7, numCell)}>{moneyCell(row.cost_now)}</td>
      <td className={c(8, numCell)}>
        <MarginValue value={marginNow(row)} lines={marginLines(row, num(row.cost_now), 'Today')} />
      </td>
      <td className={c(9, 'tabular whitespace-nowrap')}>
        <RenewalCell row={row} cost />
      </td>
      <td className={c(10, numCell)}>{moneyCell(row.cost_at_renewal)}</td>
      <td className={c(11, numCell)}>
        <MarginValue value={marginAtRenewal(row)} lines={marginLines(row, num(row.cost_at_renewal), 'From renewal')} />
      </td>
      <td className={c(12)}>
        {row.books_customer ? (
          <MaybeLink href={row.links?.books_customer}>{row.books_customer}</MaybeLink>
        ) : (
          <Missing words="no Books customer" />
        )}
      </td>
      <td className={c(13)}>
        <NoteCell note={row.key ? notes[row.key] : undefined} editHref={row.links?.patchbay} />
      </td>
    </tr>
  )
}

export function LicencesTab({ rows, notes }: { rows: LicenceMarginRow[]; notes: Notes }) {
  const [chip, setChip] = React.useState('action')
  const [q, setQ] = React.useState('')
  const [sort, setSort] = React.useState<SortState>({ key: 'renews', dir: 1 })
  const s = React.useMemo(() => licenceSummary(rows), [rows])
  const def = LICENCE_CHIPS.find((d) => d.id === chip) ?? (LICENCE_CHIPS[0] as (typeof LICENCE_CHIPS)[number])
  const shown = sortBy(
    rows.filter(
      (r) =>
        def.test(r, notes) && matchesSearch([r.customer, r.product, r.books_customer, r.books_fix, r.key ? notes[r.key]?.text : null], q),
    ),
    sort,
    sortValue,
  )
  const cls = groupClasses(COLUMNS)
  return (
    <div className="flex flex-col gap-3.5">
      <StatStrip
        label="Licence totals"
        stats={[
          moneyStat('Billed per month', s.revenue),
          moneyStat('Cost now', s.costNow),
          pctStat('Margin now', s.marginNow),
          moneyStat('Cost after renewals', s.costRenewal),
          pctStat(
            'Margin after renewals',
            s.marginRenewal,
            s.marginRenewal !== null && s.marginNow !== null && s.marginRenewal < s.marginNow ? 'warn' : undefined,
          ),
          countStat('Negative now', s.negativeNow, 'bad'),
          countStat('Negative at renewal', s.negativeRenewal, 'warn'),
          countStat('Under MSRP', s.underMsrp),
          countStat('Books lines to fix', s.booksFix, 'warn'),
        ]}
      />
      <ChipFilter label="Filter licences" defs={LICENCE_CHIPS} rows={rows} notes={notes} value={chip} onChange={setChip} />
      <SearchBox label="Search licences" value={q} onChange={setQ} count={`${shown.length} of ${rows.length} lines`} />
      <Legend />
      {rows.length === 0 ? (
        <EmptyState title="Pax8 lists no licences." detail="The sync ran and returned none." />
      ) : shown.length === 0 ? (
        <EmptyState title="No licence matches." detail="Try another filter or clear the search." />
      ) : (
        <GroupedTable label="Licences" columns={COLUMNS} sort={sort} onSort={setSort}>
          {shown.map((r, i) => (
            <LicenceRow key={r.key ?? `${r.customer}:${r.product}:${i}`} row={r} notes={notes} cls={cls} />
          ))}
        </GroupedTable>
      )}
    </div>
  )
}
